/**
 * @file 教程页页菜单
 * @author FengGuang(fengguang01@baidu.com)
 */
import { useCallback, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useLatest, useToggle } from 'react-use';

import getSelectedObjects from './utils/getSelectedObjects';

import {
    ITutorialsProjectItem,
    ITutorialsLessonItem,
    ITutorialsBreadcrumbItem,
    ITutorialsVersionItem,
    ITutorialsGroupItem
} from '../../type';

import tutorialsData from '../../tutorialsData.json';

const useMenuHooks = () => {
    const match = useRouteMatch();
    const projectId = (match.params as any).id || '';

    const [leftMenuShow, toogleLeftMenuShow] = useToggle(true);

    const [versionList] = useState<ITutorialsVersionItem[]>(() => {
        return tutorialsData.map(version => {
            return {
                version: version.version,
                groupList: version.children || []
            };
        });
    });
    const versionListRef = useLatest(versionList);
    const [versionSelectedKey, setVersionSelectedKey] = useState<string>('2.3');

    const [groupMapByVersionId] = useState<{ [key: string]: ITutorialsGroupItem[] }>(() => {
        const groupMap: { [key: string]: ITutorialsGroupItem[] } = {};
        tutorialsData.forEach(version => {
            groupMap[version.version] = version.children;
        });
        return groupMap;
    });
    const groupMapByVersionIdRef = useLatest(groupMapByVersionId);

    const [groupSelectedKey, setGroupSelectedKey] = useState<string>('');

    const groupList = useMemo(() => {
        return groupMapByVersionId[versionSelectedKey] || [];
    }, [versionSelectedKey, groupMapByVersionId]);

    const onVersionSelectedChange = useCallback(
        (key: string) => {
            const gList = groupMapByVersionIdRef.current[key] || [];
            setGroupSelectedKey(gList[0]?.id ?? '');
            setVersionSelectedKey(key);
        },
        [setVersionSelectedKey, setGroupSelectedKey, groupMapByVersionIdRef]
    );

    const [lessonMapByGroupId, setLessonMapByGroupId] = useState<{ [key: string]: ITutorialsLessonItem[] }>({});
    const lessonMapByGroupIdRef = useLatest(lessonMapByGroupId);

    const [projectMapByLessonId, setProjectMapByLessonId] = useState<{ [key: string]: ITutorialsProjectItem[] }>({});
    const projectMapByLessonIdRef = useLatest(projectMapByLessonId);

    const lessonList = useMemo(() => {
        const list = lessonMapByGroupId[groupSelectedKey] || [];
        return list.map(lesson => {
            return {
                ...lesson,
                projectList: projectMapByLessonId[lesson.id] || lesson.projectList || []
            };
        });
    }, [groupSelectedKey, lessonMapByGroupId, projectMapByLessonId]);

    const setLessonToGroup = useCallback(
        (groupId: string, lessonList: ITutorialsLessonItem[]) => {
            setLessonMapByGroupId(old => {
                const oldList = old[groupId];
                if (oldList === lessonList) {
                    return old;
                }
                return {
                    ...old,
                    [groupId]: lessonList
                };
            });
        },
        [setLessonMapByGroupId]
    );

    const setProjectToLesson = useCallback(
        (lessonId: string, projectList: ITutorialsProjectItem[]) => {
            setProjectMapByLessonId(old => {
                const oldList = old[lessonId];
                if (oldList === projectList) {
                    return old;
                }
                return {
                    ...old,
                    [lessonId]: projectList
                };
            });
        },
        [setProjectMapByLessonId]
    );

    const [lessonSelectedKeys, setLessonSelectedKeys] = useState<string[]>([]);
    const [lessonOpenKeys, setLessonOpenKeys] = useState<string[]>([]);

    const openSelectedMenu = useCallback(() => {
        const [selectedVersion, selectedGroup, selectedLesson, selectedProject] = getSelectedObjects(
            projectId,
            versionListRef.current,
            groupMapByVersionIdRef.current,
            lessonMapByGroupIdRef.current,
            projectMapByLessonIdRef.current
        );
        setVersionSelectedKey(oldKey => {
            return selectedVersion ? selectedVersion.version : oldKey;
        });
        setGroupSelectedKey(oldKey => {
            return selectedGroup ? selectedGroup.id : oldKey;
        });
        setLessonOpenKeys(oldKeys => {
            return selectedLesson ? [...oldKeys, selectedLesson.id] : oldKeys;
        });
        setLessonSelectedKeys(() => {
            let newKeys: string[] = [];
            if (selectedLesson) {
                newKeys = [...newKeys, selectedLesson.id];
            }
            if (selectedProject) {
                newKeys = [...newKeys, selectedProject.id];
            }
            return newKeys;
        });
    }, [
        projectId,
        versionListRef,
        groupMapByVersionIdRef,
        lessonMapByGroupIdRef,
        projectMapByLessonIdRef,
        setLessonOpenKeys
    ]);

    const lessonProjectId = useMemo(() => {
        const [, , , selectedProject] = getSelectedObjects(
            projectId,
            versionListRef.current,
            groupMapByVersionIdRef.current,
            lessonMapByGroupIdRef.current,
            projectMapByLessonIdRef.current
        );
        return selectedProject?.lessonProjectId;
    }, [versionListRef, groupMapByVersionIdRef, lessonMapByGroupIdRef, projectMapByLessonIdRef, projectId]);

    const breadcrumbsList = useMemo<ITutorialsBreadcrumbItem[]>(() => {
        const list: ITutorialsBreadcrumbItem[] = [];
        const [, selectedGroup, selectedLesson, selectedProject] = getSelectedObjects(
            projectId,
            versionListRef.current,
            groupMapByVersionIdRef.current,
            lessonMapByGroupIdRef.current,
            projectMapByLessonIdRef.current
        );
        if (selectedGroup && selectedLesson && selectedProject) {
            list.push({
                ...selectedGroup,
                link: ''
            });
            list.push({
                ...selectedLesson,
                link: ''
            });
            list.push({
                ...selectedProject,
                link: ''
            });
        }
        return list;
    }, [versionListRef, groupMapByVersionIdRef, lessonMapByGroupIdRef, projectMapByLessonIdRef, projectId]);

    return {
        projectId,
        leftMenuShow,
        versionList,
        versionSelectedKey,
        groupList,
        groupSelectedKey,
        lessonList,
        lessonSelectedKeys,
        lessonOpenKeys,
        breadcrumbsList,
        lessonProjectId,

        groupMapByVersionId,
        lessonMapByGroupId,
        projectMapByLessonId,

        toogleLeftMenuShow,
        onVersionChange: onVersionSelectedChange,
        setLessonSelectedKeys,
        setLessonToGroup,
        setProjectToLesson,
        onLessionSelect: setLessonSelectedKeys,
        onLessonOpenKeysChange: setLessonOpenKeys,
        openSelectedMenu,
        onGroupSelect: setGroupSelectedKey
    };
};

export default useMenuHooks;
